/**
 * Extraction Service
 * Shared utilities for extraction and approval review screens
 */

/**
 * Check if a field name represents a date field
 * @param fieldName - The name of the field to check
 * @returns true if the field is a date field
 */
export const isDateField = (fieldName: string | null | undefined): boolean => {
  if (!fieldName || typeof fieldName !== "string") {
    return false;
  }

  const dateFields = [
    "Date of Birth(DD/MM/YYYY)",
    "TIN Check Date(MM/DD/YYYY)",
    "Previous Name Date",
    "W8 Check Date",
  ];

  // Check exact matches first
  if (dateFields.includes(fieldName)) {
    return true;
  }

  // Check if field name contains 'Date' or 'date'
  return fieldName.includes("Date") || fieldName.includes("date");
};

/**
 * Get the number of days in a specific month
 * @param month - Month number (1-12)
 * @param year - Year (optional, needed for leap year calculation)
 * @returns Number of days in the month
 */
const getDaysInMonth = (month: number, year?: number): number => {
  const daysInMonth: { [key: number]: number } = {
    1: 31, // January
    2: 28, // February (29 in leap years)
    3: 31, // March
    4: 30, // April
    5: 31, // May
    6: 30, // June
    7: 31, // July
    8: 31, // August
    9: 30, // September
    10: 31, // October
    11: 30, // November
    12: 31, // December
  };

  if (month === 2 && year) {
    // Check for leap year
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return isLeapYear ? 29 : 28;
  }

  return daysInMonth[month] || 31;
};

/**
 * Format date input value to MM/DD/YYYY format
 * Automatically adds slashes as user types with intelligent validation
 * - Auto-pads month with 0 if single digit (6 → 06)
 * - Caps month at 12 (17 → 12)
 * - Validates day based on selected month (June can't be 31)
 * - Handles leap years for February
 * @param value - The input value to format
 * @returns Formatted date string in MM/DD/YYYY format
 */
export const formatDateInput = (value: string): string => {
  if (!value) return "";

  // Remove all non-digit characters
  const cleaned = value.replace(/[^\d]/g, "");

  // Don't format if empty
  if (cleaned.length === 0) return "";

  let formatted = "";

  // Process Month (MM)
  if (cleaned.length >= 1) {
    let month = cleaned.substring(0, 2);
    let monthNum = parseInt(month, 10);

    // If first digit is greater than 1, auto-pad with 0
    if (cleaned.length === 1 && monthNum > 1) {
      formatted = "0" + month + "/";
    } else if (cleaned.length >= 2) {
      // Cap month at 12
      if (monthNum > 12) {
        monthNum = 12;
        month = "12";
      } else if (monthNum === 0) {
        monthNum = 1;
        month = "01";
      } else if (monthNum < 10 && month[0] !== "0") {
        month = "0" + monthNum;
      }
      formatted = month;

      // Process Day (DD)
      if (cleaned.length >= 3) {
        formatted += "/";
        let day = cleaned.substring(2, 4);
        let dayNum = parseInt(day, 10);

        // Get max days for the selected month
        const maxDays = getDaysInMonth(monthNum);

        // If we have both digits for day
        if (cleaned.length >= 4) {
          // Cap day at max days for the month
          if (dayNum > maxDays) {
            dayNum = maxDays;
            day = dayNum.toString().padStart(2, "0");
          } else if (dayNum === 0) {
            dayNum = 1;
            day = "01";
          } else if (dayNum < 10 && day[0] !== "0") {
            day = "0" + dayNum;
          }
          formatted += day;

          // Process Year (YYYY)
          if (cleaned.length >= 5) {
            const year = cleaned.substring(4, 8);
            formatted += "/" + year;

            // Re-validate day for leap year if we have full year
            if (year.length === 4 && monthNum === 2) {
              const yearNum = parseInt(year, 10);
              const maxDaysWithYear = getDaysInMonth(2, yearNum);
              if (dayNum > maxDaysWithYear) {
                const correctedDay = maxDaysWithYear.toString().padStart(2, "0");
                formatted = month + "/" + correctedDay + "/" + year;
              }
            }
          }
        } else {
          // Only one digit entered for day
          formatted += day;
        }
      }
    } else {
      // Only one digit entered for month
      formatted = month;
    }
  }

  return formatted;
};

/**
 * Validate date format MM/DD/YYYY
 * @param dateString - The date string to validate
 * @returns Object with validation result and error message if invalid
 */
export const validateDateFormat = (
  dateString: string
): {
  isValid: boolean;
  message: string | null;
} => {
  if (!dateString || dateString.trim() === "") {
    return { isValid: true, message: null };
  }

  // Check format MM/DD/YYYY
  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

  if (!dateRegex.test(dateString)) {
    return {
      isValid: false,
      message: "Invalid date format. Expected MM/DD/YYYY",
    };
  }

  // Parse and validate actual date values
  const parts = dateString.split("/");
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  // Check if date is valid
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return {
      isValid: false,
      message: "Invalid date. Please check the day, month, and year values.",
    };
  }

  // Check reasonable year range (1900-2100)
  if (year < 1900 || year > 2100) {
    return {
      isValid: false,
      message: "Year must be between 1900 and 2100",
    };
  }

  return { isValid: true, message: null };
};

/**
 * Get placeholder text for date fields
 * @param fieldName - The name of the field
 * @returns Placeholder text or empty string
 */
export const getDatePlaceholder = (fieldName: string | null | undefined): string => {
  return isDateField(fieldName) ? "MM/DD/YYYY" : "";
};

/**
 * Handle date input change with auto-formatting
 * @param value - The new input value
 * @param setValue - Function to set the formatted value
 */
export const handleDateInputChange = (value: string, setValue: (value: string) => void): void => {
  const formatted = formatDateInput(value);
  setValue(formatted);
};
